/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component} from '@angular/core';
import {ICellRendererAngularComp} from 'ag-grid-angular';
import {CommonHelperService} from '../../service/common-helper.service';
import {isSafeWebUrl} from '../../utils/url.util';

@Component({
    selector: 'app-url-cell-renderer',
    template: `@if (url) {
        <a [href]="url" target="_blank" rel="noopener noreferrer">{{value}}</a>
    } @else {
        {{value}}
    }`,
    standalone: false
})
export class UrlCellRendererComponent implements ICellRendererAngularComp {
    public urlBase: string | null = null;
    public url: string | null = null;
    public value: string | null = null;

    agInit(params: any): void {
        this.refresh(params);
    }

    refresh(params: any): boolean {
        const field = params.colDef.field;
        const rawValue = Object.prototype.hasOwnProperty.call(params, 'value')
            ? params.value
            : CommonHelperService.getDeepValue(params.data, field);
        this.value = rawValue == null || rawValue === '' ? null : String(rawValue);
        const rendererParams = params.colDef.cellRendererParams ?? {};
        if (rendererParams.useValueAsUrl) {
            this.urlBase = null;
            this.url = this.value && isSafeWebUrl(this.value) ? this.value : null;
            return true;
        }

        this.urlBase = rendererParams.urlBase;
        if (!this.urlBase) {
            this.url = null;
            return true;
        }

        // Find all occurrences of "${...}" in the urlBase
        const match = [...this.urlBase.matchAll(/\$\{([^}]*)\}/g)];
        this.url = this.urlBase;

        for (const m of match) {
            // Use the contents of each group as the member name of the data, and replace the placeholder in the URL.
            const placeholder = m[0]; // e.g., ${id}
            const fieldName = m[1];   // e.g., id
            const fieldValue = params.data[fieldName] ?? '';
            this.url = this.url.replaceAll(placeholder, fieldValue);
        }
        if (!this.url || !isSafeWebUrl(this.url)) {
            this.url = null;
        }
        return true; // Signals ag-Grid to refresh cell content
    }
}
